// Real-time Firestore subscription for the current user's settings document.
// Returns { userSettings, setUserSettings, userSettingsLoaded }.

import React from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_LIGHT_THEME, DEFAULT_DARK_THEME } from '../themeConfig';

/**
 * Subscribes to the current user's Firestore settings document and keeps
 * `userSettings` in sync with the database.
 *
 * @param {object|null} user - Firebase Auth user object, or null when signed out
 * @param {object} defaultUserSettings - The baseline settings object used before
 *   the snapshot resolves and when no document exists.
 * @returns {{ userSettings: object, setUserSettings: Function, userSettingsLoaded: boolean }}
 */
export function useUserSettings(user, defaultUserSettings) {
    const [userSettings, setUserSettings] = React.useState(defaultUserSettings);
    const [userSettingsLoaded, setUserSettingsLoaded] = React.useState(false);

    React.useEffect(() => {
        if (!user) {
            setUserSettings(defaultUserSettings);
            setUserSettingsLoaded(false);
            return;
        }
        const userRef = doc(db, 'users', user.uid);
        const unsubscribe = onSnapshot(userRef, (snapshot) => {
            if (!snapshot.exists()) {
                setUserSettings(defaultUserSettings);
                setUserSettingsLoaded(true);
                return;
            }
            const data = snapshot.data() || {};
            setUserSettings({
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                signature: data.signature || '',
                defaultSignatureId: data.defaultSignatureId || '',
                lightTheme: data.lightTheme || DEFAULT_LIGHT_THEME,
                darkTheme: data.darkTheme || DEFAULT_DARK_THEME,
                role: data.role === 'transactionCoordinator' ? 'transactionCoordinator' : 'realtor',
                isTcModeEnabled: Boolean(data.isTcModeEnabled),
                onboardingCompleted: Boolean(data.onboardingCompleted),
                emailTemplatesDefaultSort: data.emailTemplatesDefaultSort === 'recent' ? 'recent' : 'name',
                emailTemplatesDefaultGroup: data.emailTemplatesDefaultGroup === 'none' ? 'none' : 'audience',
                emailMarketingTemplatesDefaultSort: data.emailMarketingTemplatesDefaultSort === 'recent' ? 'recent' : 'name',
                emailMarketingTemplatesDefaultGroup: data.emailMarketingTemplatesDefaultGroup === 'none' ? 'none' : 'campaign',
                termsAcceptedAt: data.termsAcceptedAt || null,
                tcOverlayDismissed: Boolean(data.tcOverlayDismissed),
                headshotUrl: data.headshotUrl || '',
                headshotStoragePath: data.headshotStoragePath || null,
                businessCardUrl: data.businessCardUrl || '',
                businessCardStoragePath: data.businessCardStoragePath || null,
                tourState: (typeof data.tourState === 'object' && data.tourState !== null)
                    ? data.tourState
                    : {},
                acceptedTermsVersion: data.acceptedTermsVersion || null,
                acceptedAt: data.acceptedAt || null,
            });
            setUserSettingsLoaded(true);
        });
        return () => unsubscribe();
    }, [user, defaultUserSettings]);

    return { userSettings, setUserSettings, userSettingsLoaded };
}
